/* eslint-disable react/no-unused-state */
import _ from 'lodash';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import cn from 'classnames';
import ContentEditable from 'react-contenteditable';

import { IframeTunnel } from 'common/utils/iframe-tunnel';
import { isLayerType } from 'common/utils/is-layer-type';
import { prependUrlProtocol, triggerFocus } from 'common/utils/helpers';
import { IFRAME_ACTIONS, COMPONENT_STATES, UNIFORM_PROPS, CARD_TYPE } from 'common/constants';
import { BBCommonProps, CardData, StoryModel, StorySettingsType } from 'types/story';
import { ClientReducerState } from 'client/reducers';
import { sendForm } from 'client/actions/api';
import { CHILDREN_KEY, getElementIdByNodeId } from 'client/components/common/BuildingBlocks/utils/common';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { withFormContext } from 'client/components/common/StoryCard/Form/withFormContext';
import { FIELD_DATA_ATTR } from 'client/components/common/BuildingBlocks/Fields/constants';
import { FormDataT } from 'client/components/common/StoryCard/Form/types';
import css from './Button.scss';

const connector = connect(
	(state: ClientReducerState) => {
		const { type: cardType, _id: cardId } = state.card as CardData;
		const story = state.story as StoryModel;

		return {
			cardSettings: story?.storyVersions.latest.settings.cards?.[cardId],
			storyId: story.id,
			cardType,
			cardId,
		};
	},
	{ sendForm }
);

type State = {
	html: string;
	htmlPrev: string;
	editing: boolean;
	submitted: boolean;
};

type Props = BBCommonProps & { className?: string } & ConnectedProps<typeof connector> & EventProviderContextT;

class Button extends React.Component<Props, State> {
	editableRef = React.createRef<HTMLElement>();

	static getDerivedStateFromProps({ children }, state) {
		const isUpdatedChildrenFromProp = children !== state.htmlPrev;
		if (isUpdatedChildrenFromProp) {
			return { html: children, htmlPrev: children };
		}
		return null;
	}

	state = {
		htmlPrev: this.props.children as string,
		html: this.props.children as string,
		editing: false,
		submitted: false,
	};

	onEditablePaste = event => {
		// cancel paste
		event.preventDefault();
		// get text representation of clipboard
		const text = (event.originalEvent || event).clipboardData.getData('text/plain');
		// insert text manually
		const command = document.queryCommandSupported('insertText') ? 'insertText' : 'paste';
		document.execCommand(command, false, text);
	};

	onEditableBlur = event => {
		const { path } = event.target.dataset;
		const { currentMediaQuery } = this.props;

		// NOTE: children is always writes to defaultState, but for certain media query
		if (isLayerType(this.props).global) {
			transmitUpdatedValueToAdmin({
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: this.state.html,
				isStory: true,
			});
		} else {
			transmitUpdatedValueToAdmin({
				path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
				value: this.state.html,
				isStory: false,
			});
		}

		this.setState({ editing: false });
	};

	onEditableChange = event => {
		this.setState({ html: event.target.value });
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);
	};

	onEditableDoubleClick = () => {
		this.setState({ editing: true }, () => {
			this.editableRef.current?.focus();
		});
	};

	get nonEditorListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			onClick: async (event: React.MouseEvent<HTMLLinkElement | HTMLButtonElement>) => {
				_.invoke(this.props.eventListeners, 'onClick', event);

				if (event.currentTarget.tagName === 'A') {
					return;
				}

				if (isSubmit(this.props)) {
					const fields = document.querySelectorAll(`[${FIELD_DATA_ATTR}]`);
					const formData = getFormData(fields, this.props.cardSettings!);

					const { isValid } = await this.props.formContext?.validate?.(formData)!;

					if (!isValid) {
						return;
					}

					try {
						this.setState({ submitted: true });

						this.props.sendForm({
							storyId: this.props.storyId,
							cardId: this.props.cardId,
							formData: Object.values(formData),
						});
					} catch (e) {
						this.setState({ submitted: false });
					}
				}

				this.props.eventProvider.events.flow(this.props._id);
			},
		};
	}

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const nodeProps = _.merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);

		/**
		 * Why do we assign "updater" to className?
		 * This is a temporary fix, to update <ContentEditable> in case of
		 * some state was provided via nodeProps. Otherwise <ContentEditable> won't be
		 * updated, because of his own shouldComponentUpdate logic, will return false
		 * if there were changed some data attributes only.
		 */
		let updater = '';
		const updaterObj = { ...nodeProps, ...this.props.stateAttrs };
		Object.keys(updaterObj).forEach(k => {
			const res = k.match(/^(data-)/);
			if (res) updater += `${k}=${updaterObj[k]}`;
		});

		return (
			<ContentEditable
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				innerRef={this.editableRef}
				className={cn(css.btn, css.editable, uiConfig.nodeProps.className, updater)}
				tagName="div"
				html={this.state.html}
				disabled={!this.state.editing}
				onDoubleClick={this.onEditableDoubleClick}
				onChange={this.onEditableChange}
				onBlur={this.onEditableBlur}
				onPaste={this.onEditablePaste}
			/>
		);
	}

	renderDefault(props: any = {}) {
		const { nodeProps } = this.props.uiConfig;
		const { Component, ...componentProps } = getComponent(this.props);

		return (
			<Component
				{...componentProps}
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.nonEditorListeners}
				{...props}
				className={cn(css.btn, nodeProps.className, props.className)}
				/* eslint-disable-next-line react/no-danger */
				dangerouslySetInnerHTML={{ __html: this.state.html }}
				ref={this.props.containerRef}
				disabled={this.state.submitted}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

function transmitUpdatedValueToAdmin({ path, value, isStory }) {
	const iframeTunnel = new IframeTunnel({ id: 'Button' });
	iframeTunnel.messageTransmitter({
		target: IframeTunnel.targets.admin,
		action: isStory ? IFRAME_ACTIONS.UPDATE_STORY_ELEMENT_BY_PATH : IFRAME_ACTIONS.UPDATE_CARD_ELEMENT_BY_PATH,
		payload: {
			path,
			value,
		},
	});
}

function isSubmit(props: Props) {
	return !!props.uiConfig.componentProps[UNIFORM_PROPS.btnSubmit] && props.cardType === CARD_TYPE.FORM;
}

function getLink(props: Props) {
	return props.uiConfig.componentProps[UNIFORM_PROPS.btnLink];
}

function getComponent(props) {
	const link = getLink(props);

	if (link && !isSubmit(props)) {
		const target = props.uiConfig.componentProps[UNIFORM_PROPS.btnTarget];

		return {
			Component: 'a',
			href: prependUrlProtocol(link),
			target: target ? '_blank' : '_self',
			rel: target ? 'noopener noreferrer' : undefined,
		};
	}

	return {
		Component: 'button',
		type: 'button',
	};
}

function getFormData(
	fields: NodeListOf<Element>,
	cardSettings: NonNullable<StorySettingsType['cards']>[string]
): FormDataT {
	return _.reduce(
		fields,
		(memo, field) => {
			const { value, type, checked } = field;
			const nodeId = field.dataset.bb ? field.id : field.closest('[data-bb]').id;
			const id = getElementIdByNodeId(nodeId);
			const name = cardSettings?.input?.[id]?.name || field.name;

			switch (type) {
				case 'checkbox':
					if (!memo[id]) {
						_.set(memo, id, { name, type, data: value, attributes: { id, checked } });
					}
					break;
				case 'radio':
					_.set(memo, id, { name, type, data: value, attributes: { id, checked } });
					break;
				default:
					_.set(memo, id, { name, type, data: value, attributes: { id } });
					break;
			}

			return memo;
		},
		{}
	);
}

const WithRedux = connector(Button);
const WithFormContext = withFormContext(WithRedux);
const WithCardTransitionContext = withCardTransitionContext(WithFormContext);

export default withEventProvider(WithCardTransitionContext);
